import React, { useMemo } from 'react';

import { DateBadge, StatCard } from '../components/base';
import { OriginFunnelSection } from '../features/funil/OriginFunnelSection';
import { SellerFunnelSection } from '../features/funil/SellerFunnelSection';
import { baixarCSV, leadParaLinha } from '../lib/exportar';
import { fmt } from '../lib/formato';
import { computeKPIs } from '../lib/pipeline';

function KPIsView({ leads, sellers, onOpenLead, company }) {
  const kpis = useMemo(() => computeKPIs(leads), [leads]);
  const maxCount = Math.max(...kpis.by_stage.map(s=>s.count), 1);
  return <div className="space-y-6">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-lg font-bold text-gray-800">KPIs do Pipeline</h2>
      <button
        onClick={()=>baixarCSV('kpis-pipeline', leads.map(leadParaLinha))}
        className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 text-gray-600 rounded-xl px-3 py-2 hover:bg-gray-50 transition-colors">
        <span aria-hidden="true">⬇</span> Exportar CSV
      </button>
    </div>

    {/* C-33, C-54, C-69 */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatCard label="🎯 Pipeline Ponderado" value={fmt(kpis.weighted_pipeline)} color="text-purple-700"
        sub="valor × probabilidade" />
      <StatCard label="⏳ Esfriando" value={kpis.esfriando_count}
        color={kpis.esfriando_count>0?'text-amber-600':'text-gray-500'} sub="parados além do limite da etapa" />
      <StatCard label="⚡ 1º Contato" value={kpis.avg_primeiro_contato>0?`${kpis.avg_primeiro_contato.toFixed(1)}h`:'—'}
        color="text-blue-600" sub="média até a primeira interação" />
      <StatCard label="🔇 Sem Contato" value={kpis.sem_primeiro_contato}
        color={kpis.sem_primeiro_contato>0?'text-red-500':'text-gray-500'} sub="ativos nunca abordados" />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <StatCard label="Total de Leads"        value={kpis.total_leads} />
      <StatCard label="Pipeline Ativo"        value={fmt(kpis.pipeline_value)} color="text-blue-700"
        sub={kpis.pipeline_total_contract !== kpis.pipeline_value ? `Contrato total: ${fmt(kpis.pipeline_total_contract)}` : null} />
      <StatCard label="Receita Fechada"       value={fmt(kpis.closed_value)} color="text-green-700"
        sub={kpis.closed_total_contract !== kpis.closed_value ? `Contrato total: ${fmt(kpis.closed_total_contract)}` : null} />
      <StatCard label="Win Rate"              value={`${kpis.win_rate.toFixed(1)}%`} color={kpis.win_rate>=50?"text-green-600":"text-orange-500"} />
      <StatCard label="Conversão em Proposta" value={`${kpis.proposal_conversion_rate.toFixed(1)}%`} color="text-purple-600" />
    </div>
    {(kpis.pipeline_total_contract !== kpis.pipeline_value || kpis.closed_total_contract !== kpis.closed_value) && (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="💰 Contrato Total (Pipeline)" value={fmt(kpis.pipeline_total_contract)} color="text-blue-700" sub="soma de todos os contratos" />
        <StatCard label="🏆 Contrato Total (Fechados)" value={fmt(kpis.closed_total_contract)} color="text-green-700" sub="soma de todos os contratos" />
        <StatCard label="Ticket Médio (Contrato)"  value={kpis.avg_ticket>0?fmt(kpis.avg_ticket):'—'} color="text-teal-700" sub="por cliente fechado" />
        <StatCard label="Leads Fechados"        value={kpis.closed_count} color="text-green-600" sub={`de ${kpis.active_count} ativos`} />
      </div>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <StatCard label="Leads Ativos"    value={kpis.active_count} color="text-blue-600" />
      <StatCard label="Fechados"        value={kpis.closed_count} color="text-green-600" />
      <StatCard label="Perdidos"        value={kpis.lost_count} color={kpis.lost_count>0?"text-red-500":"text-gray-500"} />
      <StatCard label="Lead Time Médio" value={kpis.avg_lead_time_days>0?`${kpis.avg_lead_time_days.toFixed(0)}d`:'—'} sub="dias até fechar" />
      <StatCard label="Ticket Médio"    value={kpis.avg_ticket>0?fmt(kpis.avg_ticket):'—'} color="text-teal-700" />
    </div>
    <div className="acard rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Funil por Etapa</h3>
      <div className="space-y-2">
        {kpis.by_stage.map(s=>(
          <div key={s.key} className="flex items-center gap-3">
            <div className="text-xs text-gray-500 w-28 truncate">{s.label}</div>
            <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
              <div className="h-full bg-blue-400 rounded-full transition-all" style={{width:`${(s.count/maxCount)*100}%`}} />
            </div>
            <div className="text-xs font-semibold text-gray-700 w-6 text-right">{s.count}</div>
            <div className="text-xs text-gray-400 w-28 text-right">
              {s.value>0 ? (s.total_contract && s.total_contract !== s.value ? <span title={`Contrato total: ${fmt(s.total_contract)}`}>{fmt(s.value)}<span className="ml-1 text-blue-400">({fmt(s.total_contract)})</span></span> : fmt(s.value)) : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
    <OriginFunnelSection leads={leads} onOpenLead={onOpenLead} company={company} />
    <SellerFunnelSection leads={leads} sellers={sellers||[]} onOpenLead={onOpenLead} company={company} />
    <div className="acard rounded-2xl p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">📅 Agenda de Abordagens</h3>
      {kpis.next_contacts.length===0
        ? <p className="text-sm text-gray-400 text-center py-4">Nenhuma data de abordagem configurada</p>
        : <div className="space-y-4">
            {['overdue','today','upcoming'].map(status=>{
              const group = kpis.next_contacts.filter(c=>c.status===status);
              if (!group.length) return null;
              const labels = {overdue:'⚠️ Atrasados',today:'🔔 Hoje',upcoming:'📅 Próximos'};
              const clss   = {overdue:'text-red-600',today:'text-orange-600',upcoming:'text-blue-600'};
              return <div key={status}>
                <p className={`text-xs font-bold mb-2 ${clss[status]}`}>{labels[status]}</p>
                <div className="space-y-1">
                  {group.map(c=>(
                    <div key={c.id} onClick={()=>onOpenLead(c.id)} className="flex items-center justify-between bg-black/[0.03] rounded-xl px-3 py-2 cursor-pointer hover:bg-black/[0.06] transition-colors">
                      <span className="text-sm font-medium text-gray-800">{c.company_name}</span>
                      <DateBadge dateStr={c.next_contact_date} />
                    </div>
                  ))}
                </div>
              </div>;
            })}
          </div>}
    </div>
  </div>;
}

// ─── NotificationBell ────────────────────────────────────

export { KPIsView };
